import { PageMeta } from 'constants/pages';
import { ArticleSections } from 'types/articles';
import { EHashtag, TCardProps } from 'types/common';

import BoatImage from 'assets/news/first-september-2025-news/boat.webp';
import DSEIUKImage from 'assets/news/first-september-2025-news/dsei-uk.webp';
import MSPOImage from 'assets/news/first-september-2025-news/mspo.webp';

const commonData = {
  title: 'Takeaways from MSPO & DSEI UK: Insights from Our Head of Sales',
  publishDate: 'September 29, 2025',
  minutesOfRead: 3,
};

export const firstSeptember2025NewsMeta: PageMeta = {
  url: '/company-updates/takeaways-from-mspo-&-dsei-uk-insights-from-our-head-of-sales',
  title: commonData.title,
  description:
    'MSPO 2025, DSEI UK 2025, defense simulation, defense technology trends, unmanned systems, autonomous defense, synthetic data defense, AI in defense, simulation for defense, military innovation, defense exhibitions Europe',
};

export const firstSeptember2025NewsData: ArticleSections[] = [
  {
    resourceType: 'Company Updates',
    image: BoatImage,
    text: (
      <>
        <p>
          This September, AILiveSim had the privilege of attending <strong>MSPO in Poland</strong>{' '}
          and <strong>DSEI in London</strong>, two of the most important defense and security
          exhibitions in Europe. Both events brought together industry leaders, innovators, and
          decision-makers shaping the future of defense technology.
        </p>

        <p>Our Head of Sales, Jukka Puputti, shared a few key takeaways:</p>

        <p>
          <strong>Strong demand for simulation in defense</strong>
          <br />
          Defense companies are seeking reliable, cost-effective simulation environments to
          accelerate development and validation of AI and autonomous systems.
        </p>

        <p>
          <strong>Unmanned systems everywhere</strong>
          <br />
          Both events showcased established players alongside a surge of startups and scale-ups
          developing unmanned surface, underwater, and ground vehicles. The ecosystem is expanding
          fast.
        </p>

        <p>
          <strong>Wide spectrum of autonomy</strong>
          <br />
          We observed that autonomy comes in many “flavors.” Some systems are unmanned but not
          autonomous at all, while others are moving closer to fully autonomous missions. Maturity
          levels vary: a few vendors are just starting out, while others claim high autonomy, though
          reality can differ.
        </p>

        <p>
          <strong>Growing role of synthetic data</strong>
          <p>
            From sensor fusion to computer vision, synthetic data is no longer optional, it’s
            becoming a strategic enabler for training and validating models in complex, all-weather
            conditions.
          </p>
        </p>

        <p>
          <strong>Collaboration is key</strong>
          <br />
          Both MSPO and DSEI reinforced the need for partnerships across simulation, AI, and
          hardware to deliver scalable, end-to-end solutions.
        </p>

        <p>
          For AILiveSim, these events confirmed our commitment to providing high-fidelity simulation
          and intelligent testing solutions for defense and security applications. We look forward
          to continuing to support innovation and building strong collaborations in this critical
          domain.
        </p>

        <p>
          👉 If you’d like to learn more about how simulation can accelerate defense technology
          development, let’s connect.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '32px 0 16px' }}>
          <div>
            <img
              src={DSEIUKImage}
              alt="dsei-uk"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                display: 'block',
                marginRight: '16px',
              }}
            />
          </div>

          <div>
            <img
              src={MSPOImage}
              alt="mspo"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                display: 'block',
              }}
            />
          </div>
        </div>
      </>
    ),
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    ...commonData,
  },
];

export const firstSeptember2025NewsCard: TCardProps = {
  description:
    'Insights from MSPO & DSEI UK: AILiveSim shares key defense tech trends—simulation demand, unmanned systems, autonomy, synthetic data, and collaboration.',
  image: BoatImage,
  type: 'Company Updates',
  category: 'company-updates',
  postUrl: firstSeptember2025NewsMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AISimulation,
    EHashtag.AutonomousVehicles,
    EHashtag.DefenseTechnology,
    EHashtag.Robotics,
  ],
};
